import {
  isEmptyString,
} from '@populateui/utils';
import isArray from 'lodash/isArray';
import {
  AddressDTO,
} from 'dtos';
import {
  validateEmail,
} from 'pages/Dashboard/utils/helper';
import dayjs from 'utils/dayjs';
import {
  getAddressString,
} from 'utils/string';
import {
  FillableFormField,
  FormDynamicField,
  FormFieldValue,
} from 'views/EMR/components/FillableForm/types';
import {
  isFormFieldStrictlyRequired,
} from 'views/EMR/components/FillableForm/utils/fieldPositioning';
import {
  isFormDynamicField,
} from 'views/EMR/components/FillableForm/utils/normalizeFieldValues';

const validatorsByType: Record<string, (val?: string | boolean | AddressDTO | string[] | null) => boolean> = {
  email: (val) => validateEmail(val as string),
  phone: (val) => /^\d{11}$/i.test((val as string)?.replace(/\D/g, '') ?? ''),
  checkbox: (val) => val === true,
  checkboxGroup: (val) => isArray(val) && val.length > 0,
  date: (val) => !isEmptyString(val as string) && dayjs(val as string).isValid(),
  signatureTimestamp: (val) => !isEmptyString(val as string),
  ssn: (val) => (val as string)?.length === 11,
  ein: (val) => (val as string)?.length === 10,
  usAddress: (val) => getAddressString(val as AddressDTO) !== '—',
};

function getValidatedFieldValue(data: FormFieldValue): string | boolean | AddressDTO | string[] | null | undefined {
  if (isFormDynamicField(data)) {
    return data?.value;
  }

  if (typeof data === 'object' && data !== null && 'value' in data) {
    return (data as FormDynamicField).value;
  }

  return undefined;
}

export function getFormFieldValidationMessage(field: FillableFormField): string {
  switch (field.type) {
    case 'checkbox':
      return 'Please check this box to continue.';
    case 'radioGroup':
      return 'Please select an option.';
    case 'checkboxGroup':
      return 'Please select at least one option.';
    case 'email':
      return 'Please enter a valid email address.';
    case 'phone':
      return 'Please enter a valid phone number.';
    case 'date':
    case 'signatureDate':
      return 'Please enter a valid date.';
    case 'usAddress':
      return 'Please enter a valid address.';
    default:
      return 'This field is required.';
  }
}

export default function validateFormFieldValue(data: FormFieldValue, field: FillableFormField): boolean {
  if (!isFormFieldStrictlyRequired(field)) {
    return true;
  }

  const value = getValidatedFieldValue(data);
  const validateFn = validatorsByType[field.type] ?? ((_value) => !isEmptyString(_value as string));
  return validateFn(value);
}
